import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TforensicTimesZone } from 'src/app/domain/tforensicTimesZone';
import { ForensicUserProfileService } from 'src/app/service/forensicUserProfile.service';
import { TimeZoneService } from 'src/app/service/time-zone.service';
import { UserProfileService } from 'src/app/service/user-profile.service';
import { BaseDialogComponent } from 'src/app/shared/base/dialog/dialog.component';


@Component({
  selector: 'app-setup-timezone',
  templateUrl: './timezone.component.html',
  styleUrls: ['./timezone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,

})

// ===========================================================
// Extends Base Dialog Component in Shared/Base folder.
// ===========================================================
export class TimezoneComponent extends BaseDialogComponent implements OnInit {

  @Output() timeZoneSetEvent = new EventEmitter<string>();

  updateParentTimeZoneSet(timeZone: string) {
    console.log("UpdateParent with ", timeZone);
    this.timeZoneSetEvent.emit(timeZone);
  }

  isTimeZoneSet: boolean = false;

  alltimeZones: TforensicTimesZone[];


  constructor(
    private forensicUserProfileService: ForensicUserProfileService,
    private userProfileService: UserProfileService,
    private toastr: ToastrService,
    private _timeZoneService: TimeZoneService,
    public readonly cdRef: ChangeDetectorRef

  ) {
    super(cdRef);
  }

  ngOnInit(): void {



  }

  load() {
    console.log("Getting TimeZone info from Server");

    // Load TimeZones
    this._timeZoneService.getAllTimezones().subscribe((r) => {
      console.log("Got Timezone Info", r);
      this.alltimeZones = r.result;

    this.isLoading = false;
      this.cdRef.detectChanges();
    });
  }


  // ---------------------------------------------------------------------------
  // The PATCH is only half the job. The API derives IanaTimeZoneId from the
  // Windows id server-side, and UserProfileService.user is the single copy the
  // Settings dropdown and the "Your time zone is not set" banner both read. Left
  // unrefreshed it keeps the pre-setup blanks for the whole browser session, so
  // a user who just picked a zone is told they have none while their notes are
  // already rendering in it. Read the saved profile back before telling the
  // parent the step is done.
  //
  // A successful read is NOT the same as a fresh one. The API caches the profile
  // for ten minutes in the serving instance's own IMemoryCache and the time zone
  // write patches only that instance's copy, so on a scaled-out API the read-back
  // can be served from another instance's pre-save cache. `didProfileTakeTheZone`
  // checks the value that came back rather than trusting the 200, which is the
  // same rule the rest of this product follows: completion is learned from the
  // server, never assumed.
  // ---------------------------------------------------------------------------
  SaveTimeZone(timeZone: string) {
    console.log("SaveTimeZone Clicked", timeZone);

    this.isSaveButtonDisabled = true;

    this.forensicUserProfileService.setTimeZone(timeZone)
      .pipe(switchMap(() => this.userProfileService.reloadFromServer()))
      .subscribe((reload) => {

        console.log("SaveTimeZone Saved = ", timeZone);

        // The zone is stored either way. isTimeZoneSet releases the dialog's
        // close guard on the strength of the PATCH alone, because that is what
        // it is about: the user HAS made a choice and must not be trapped here.
        this.isTimeZoneSet = true;
        this.isSaveButtonDisabled = false;

        if (reload.reloaded === false || !this.didProfileTakeTheZone(timeZone)) {
          // Saved on the server, but this tab is not holding that value. Say both
          // halves: the choice is not lost, and the screen may still be stale.
          // hide() runs BEFORE any parent notification and the parent is
          // deliberately not notified at all here, because its handler re-runs
          // the account-setup checks against this stale copy, reopens this very
          // dialog, and the hide() that followed would then close it back down
          // with nothing on screen to explain why.
          console.error("SaveTimeZone --> profile not refreshed", reload);
          this.hide();
          this.toastr.warning("Your time zone was saved, but this page could not refresh." +
            "<br><br>Reload the page (F5) if dates still look wrong.", "TIME ZONE",
            { enableHtml: true });
          return;
        }

        this.hide();
        this.updateParentTimeZoneSet(timeZone);


      },
        (error: HttpErrorResponse) => {
          console.error("ERROR --->");
          console.error(error);
          this.isSaveButtonDisabled = false;
          this.toastr.error("Failed to Save... please contact support", "ERROR");
        });


  }

  /** TRUE when the profile this browser now holds carries the zone just saved. */
  private didProfileTakeTheZone(timeZone: string): boolean {
    return this.userProfileService.user.timeZone === timeZone;
  }

  onDialogClose(event) {

    if (this.isTimeZoneSet == false) {
      // Do not allow Dialog to Close with X button
      this.toastr.warning("Sorry, but you must set your TimeZone in order for your notes to be displayed." +
      "<br><br>If you have any questions, please contact support@forensicnotes.com", "TIME ZONE", 
        { enableHtml: true });
      event.cancel = true;    // To cancel closing of the dialog
    }
    // else.. allow close if terms accepted
  }


}
